import { useLocation, useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'material-react-toastify';
import { sharedColors, sharedStyles } from '../../../../utils/Style';
import { UpdateProductTabProps } from '../../../../interfaces/Product/UpdateProductTab';
import { RelatedProductCandidate } from '../../../../interfaces/RelatedProduct/RelatedProductCandidate';
import {
  getRelatedProducts,
  upsertRelatedProducts,
} from '../../../../api/RelatedProduct';
import { RelatedProductChip } from './RelatedProductChip';
import { Waiting } from '../../../../components/Waiting/Waiting';

export const UpdateRelatedProducts = (props: UpdateProductTabProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const goBack = () => navigate(`/products/catalog${location.search}`);

  const [candidates, setCandidates] = useState<RelatedProductCandidate[]>([]);
  const [relatedProducts, setRelatedProducts] = useState<
    RelatedProductCandidate[]
  >([]);
  const [loading, setLoading] = useState(true);

  const availableOptions = useMemo(
    () => candidates.filter((x) => !relatedProducts.some((y) => x.id === y.id)),
    [candidates, relatedProducts],
  );

  const refreshRelatedProducts = () => {
    setLoading(true);
    getRelatedProducts(props.productID)
      .then((res) => {
        setCandidates(res.candidates);
        setRelatedProducts(
          res.relatedProductIDs
            .map((id) => res.candidates.find((x) => x.id === id))
            .filter((x) => !!x),
        );
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshRelatedProducts();
  }, []);

  const handleDelete = (id: number) => {
    setRelatedProducts(relatedProducts.filter((x) => x.id !== id));
  };

  const handleSelect = (entry: RelatedProductCandidate) => {
    setRelatedProducts([...relatedProducts, entry]);
  };

  const handleSubmit = () => {
    setLoading(true);
    upsertRelatedProducts(
      props.productID,
      relatedProducts.map((x) => x.id),
    )
      .then(() => {
        toast.success('İlgili ürünler başarıyla güncellendi');
        refreshRelatedProducts();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Box sx={{ ...sharedStyles.pageBody, p: 3 }}>
      <Waiting open={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>İlgili Ürünler</Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        {relatedProducts.map((p, i) => (
          <RelatedProductChip
            key={i}
            entry={p}
            onDelete={() => handleDelete(p.id)}
          />
        ))}
      </Box>

      <Autocomplete
        renderInput={(params) => (
          <TextField
            {...params}
            label='Ürün'
            variant='outlined'
            sx={{ width: 350, mt: 1.5, backgroundColor: sharedColors.white }}
          />
        )}
        options={availableOptions}
        getOptionLabel={(option: RelatedProductCandidate) => option.name}
        value={null}
        onChange={(_, p) => {
          if (p) {
            handleSelect(p);
          }
        }}
      />

      <Button
        variant='contained'
        onClick={handleSubmit}
        sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
      >
        Kaydet
      </Button>
    </Box>
  );
};
